import React, { useState, useEffect, useRef } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import {
    Card,
    CardContent,
    Typography,
    TextField,
    IconButton,
    Box,
    Paper,
    CircularProgress,
    Alert,
    List,
    ListItem,
    ListItemButton,
    Divider,
    Drawer,
    Button,
    Chip,
    Tooltip,
} from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import HistoryIcon from '@mui/icons-material/History';
import CloseIcon from '@mui/icons-material/Close';
import ChatIcon from '@mui/icons-material/Chat';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import PeopleIcon from '@mui/icons-material/People';
import SummarizeIcon from '@mui/icons-material/Summarize';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import PsychologyIcon from '@mui/icons-material/Psychology';
import EmojiObjectsIcon from '@mui/icons-material/EmojiObjects';
import ReportProblemOutlinedIcon from '@mui/icons-material/ReportProblemOutlined';
import DownloadIcon from '@mui/icons-material/Download';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import ModernToggle from '../ModernToggle';
import ChatSettingsButton from './ChatSettingsButton';
import useChatLlmProvider from '../../hooks/useChatLlmProvider';
import Citations from './Citations';
import RiskScoreCards from './RiskScoreCards';
import ChatMessageMeta from './ChatMessageMeta';
import MessageActionsRow from './MessageActionsRow';
import TypingIndicator from './TypingIndicator';
import createChunkReplay from './chunkReplay';
import AnswerExportMenu from './AnswerExportMenu';
import { exportAnswerAsPdf, exportAnswerAsWord, exportTimestamp } from './answerExport';

import useStore from '../../store';
import insightsApi from '../../api/insightsApi';
import { token_keys } from '../../constants';

// Base URL for backend
const BACKEND_BASE_URL =
    process.env.REACT_APP_BACKEND_URL ||
    (process.env.NODE_ENV === 'development'
        ? 'http://localhost:8000'
        : null);

if (!BACKEND_BASE_URL) {
    console.error('BACKEND_BASE_URL is missing');
}

// Convert http(s) URL to ws(s) URL for WebSocket connection
const getWebSocketUrl = (httpUrl) => {
    if (httpUrl.startsWith('https://')) {
        return httpUrl.replace('https://', 'wss://');
    }
    if (httpUrl.startsWith('http://')) {
        return httpUrl.replace('http://', 'ws://');
    }
    return httpUrl;
};

// Convert topics data to themes format (formatMessage for i18n)
const convertTopicsToThemes = (topicsData, formatMessage) => {
    if (!topicsData || typeof topicsData !== 'object') return [];

    const themes = [];
    const DEFAULTS = {
        'askPrompta.untitled_topic': 'Untitled Topic',
        'askPrompta.pillar_default': 'General',
        'askPrompta.suggested_question_concerns': 'What are the main concerns mentioned under "{topic}"?',
        'askPrompta.suggested_question_vary': 'How does "{topic}" vary by role or tenure?',
    };
    const fmt = (id, values = {}) =>
        formatMessage
            ? formatMessage({ id, defaultMessage: DEFAULTS[id] || id }, values)
            : (DEFAULTS[id] ? DEFAULTS[id].replace(/\{(\w+)\}/g, (_, k) => values[k] || '') : id);

    Object.entries(topicsData).forEach(([questionText, topics]) => {
        if (!Array.isArray(topics)) return;

        topics.forEach(topic => {
            const topicTitle = topic.title || fmt('askPrompta.untitled_topic');
            // Extract keywords from topic.keywords object (format: {keyword: weight})
            const keywordsList = topic.keywords
                ? Object.entries(topic.keywords)
                    .sort((a, b) => b[1] - a[1]) // Sort by weight
                    .slice(0, 6)
                    .map(([word]) => word)
                : [];

            themes.push({
                question_text: questionText,
                topic_text: topicTitle,
                pillar: topic.pillar || fmt('askPrompta.pillar_default'),
                num_of_responses: topic.count || 0,
                keywords: keywordsList,
                suggested_questions: [
                    fmt('askPrompta.suggested_question_concerns', { topic: topic.title || topicTitle }),
                    fmt('askPrompta.suggested_question_vary', { topic: topic.title || topicTitle })
                ]
            });
        });
    });

    // Sort by number of responses (most responses first)
    return themes.sort((a, b) => b.num_of_responses - a.num_of_responses);
};

const QUICK_PROMPTS = [
    {
        id: 'summary',
        icon: SummarizeIcon,
        labelId: 'askPrompta.quick_prompt_summary_label',
        labelDefault: 'Summary',
        descriptionId: 'askPrompta.quick_prompt_summary_desc',
        descriptionDefault: 'Concise overview of the key themes and findings in this report.',
        color: '#1565c0',
        bg: 'rgba(21, 101, 192, 0.06)',
        border: 'rgba(21, 101, 192, 0.18)',
    },
    {
        id: 'risk_summary',
        icon: ReportProblemOutlinedIcon,
        labelId: 'askPrompta.quick_prompt_risk_summary_label',
        labelDefault: 'Risk Summary',
        descriptionId: 'askPrompta.quick_prompt_risk_summary_desc',
        descriptionDefault: 'Whole-report average risk levels and their key drivers.',
        color: '#c62828',
        bg: 'rgba(198,40,40,0.06)',
        border: 'rgba(198,40,40,0.18)',
        isRisk: true,
    },
    {
        id: 'change_plan',
        icon: TrendingUpIcon,
        labelId: 'askPrompta.quick_prompt_change_plan_label',
        labelDefault: 'Change Plan',
        descriptionId: 'askPrompta.quick_prompt_change_plan_desc',
        descriptionDefault: 'Structured action plan based on the report insights.',
        color: '#2e7d32',
        bg: 'rgba(46, 125, 50, 0.06)',
        border: 'rgba(46, 125, 50, 0.18)',
    },
    {
        id: 'comms_plan',
        icon: PsychologyIcon,
        labelId: 'askPrompta.quick_prompt_comms_label',
        labelDefault: 'Communication Plan',
        descriptionId: 'askPrompta.quick_prompt_comms_desc',
        descriptionDefault: 'Communication and engagement strategy based on findings.',
        color: '#6a1b9a',
        bg: 'rgba(106, 27, 154, 0.06)',
        border: 'rgba(106, 27, 154, 0.18)',
    },
    {
        id: 'training_plan',
        icon: EmojiObjectsIcon,
        labelId: 'askPrompta.quick_prompt_training_label',
        labelDefault: 'Training Plan',
        descriptionId: 'askPrompta.quick_prompt_training_desc',
        descriptionDefault: 'Training recommendations based on capability gaps identified.',
        color: '#e65100',
        bg: 'rgba(230, 81, 0, 0.06)',
        border: 'rgba(230, 81, 0, 0.18)',
    },
    {
        id: 'leader_talking_points',
        icon: TrendingUpIcon,
        labelId: 'askPrompta.quick_prompt_leader_tp_label',
        labelDefault: 'Leader Talking Points',
        descriptionId: 'askPrompta.quick_prompt_leader_tp_desc',
        descriptionDefault: 'Tailored talking points, by audience, about leadership.',
        color: '#00695c',
        bg: 'rgba(0, 105, 92, 0.06)',
        border: 'rgba(0, 105, 92, 0.18)',
    },
];

const AskPrompta = ({ reportId, topicsData, hasRiskData = false }) => {
    const intl = useIntl();
    const [userInput, setUserInput] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    // What the BE agent is looking up right now ({ tool, label }), for the loading state
    const [agentStatus, setAgentStatus] = useState(null);
    const [error, setError] = useState(null);
    const [llmProvider, setLlmProvider] = useChatLlmProvider();

    const [isConnected] = useState(true);
    const [historyDrawerOpen, setHistoryDrawerOpen] = useState(false);
    const [chatSessions, setChatSessions] = useState([]);
    const [loadingHistory, setLoadingHistory] = useState(false);
    const [selectedSessionId, setSelectedSessionId] = useState(null);

    // Toggle between Quick Prompts (default) and Topics views
    const [showTopics, setShowTopics] = useState(false);

    // Risk mode: when true, the Risk Summary quick prompt and freeform
    // questions send scope:'risk' so the BE answers from risk data only.
    const [riskMode, setRiskMode] = useState(false);
    const [downloadAnchor, setDownloadAnchor] = useState(null);
    const [downloadTarget, setDownloadTarget] = useState(null);

    // Theme suggestions state
    const [themes, setThemes] = useState([]);
    const [currentThemePage, setCurrentThemePage] = useState(0);
    const THEMES_PER_PAGE = 2;

    const messagesEndRef = useRef(null);
    const websocketRef = useRef(null);
    // paces a cached answer through the streamed path — see chunkReplay
    const replayRef = useRef(null);

    // Stop the current answer: close its socket and drop the rest of a cached
    // answer's replay, so either kind of answer is interrupted the same way.
    // Forgetting both refs is what stops it for good: a stream's handlers act
    // only while it is still the current one (see startWebSocketStream).
    const stopStream = () => {
        replayRef.current?.cancel();
        replayRef.current = null;
        const ws = websocketRef.current;
        websocketRef.current = null;
        ws?.close();
    };

    // ---- Ask-Prompta store wiring ----
    const channel = 'insights_generation';
    const askState = useStore((state) => state.askPrompta[`${reportId}::${channel}`]);
    const initSession = useStore((state) => state.initAskPromptaSession);
    const appendUserMessage = useStore((state) => state.appendUserMessage);
    const appendQaHistoryEntry = useStore((state) => state.appendQaHistoryEntry);
    const appendAssistantChunk = useStore((state) => state.appendAssistantChunk);
    const attachAssistantDocuments = useStore((state) => state.attachAssistantDocuments);
    const attachAssistantRiskMeta = useStore((state) => state.attachAssistantRiskMeta);
    const finalizeAssistantMessage = useStore((state) => state.finalizeAssistantMessage);
    const loadHistorySession = useStore((state) => state.loadHistorySession);
    const ensureMessageId = useStore((state) => state.ensureMessageId);
    const setMessageFeedback = useStore((state) => state.setMessageFeedback);

    const messages = askState?.messages || [];
    const qaHistory = askState?.qaHistory || [];
    const sessionId = askState?.sessionId;
    const isViewingHistory = askState?.isViewingHistory;
    const clearAskPromptaMessages = useStore(
        (state) => state.clearAskPromptaMessages
    );

    // ---- scroll helpers ----
    const scrollContainerRef = useRef(null);
    const isAtBottom = useRef(true);

    const scrollToBottom = () => {
        // block: 'nearest' scrolls the minimum needed — 'end' over-scrolled
        // and clipped the typing indicator below the viewport edge
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    };

    const handleScroll = () => {
        const el = scrollContainerRef.current;
        if (!el) return;
        const threshold = 40;
        isAtBottom.current = el.scrollHeight - el.scrollTop - el.clientHeight <= threshold;
    };

    // isLoading must be a trigger too: the typing indicator mounts on its
    // own when isLoading flips without a messages change (e.g. quick
    // prompts stream without appending a user message first)
    useEffect(() => {
        if (isAtBottom.current) {
            scrollToBottom();
        }
    }, [messages, isLoading]);

    useEffect(() => {
        if (isViewingHistory && messages.length > 0) {
            isAtBottom.current = true;
            scrollToBottom();
        }
    }, [isViewingHistory, messages.length]);

    // init new session on mount / refresh
    useEffect(() => {
        if (reportId && !askState) {
            initSession(reportId, channel);
        }

        return () => {
            clearAskPromptaMessages(reportId, channel);
            stopStream();
        };
    }, [reportId]);

    // Convert topics data to themes when topicsData or locale changes
    useEffect(() => {
        if (topicsData) {
            const convertedThemes = convertTopicsToThemes(topicsData, (desc, values) =>
                intl.formatMessage(desc, values)
            );
            setThemes(convertedThemes);
            setCurrentThemePage(0); // Reset to first page when data changes
        }
    }, [topicsData, intl]);

    // Theme navigation
    const totalThemePages = Math.ceil(themes.length / THEMES_PER_PAGE);
    const currentThemes = themes.slice(
        currentThemePage * THEMES_PER_PAGE,
        (currentThemePage + 1) * THEMES_PER_PAGE
    );

    const handlePrevThemes = () => {
        setCurrentThemePage(prev => Math.max(0, prev - 1));
    };

    const handleNextThemes = () => {
        setCurrentThemePage(prev => Math.min(totalThemePages - 1, prev + 1));
    };

    const handleSuggestedQuestion = (question) => {
        setUserInput(question);
    };

    const buildHistoryForRAG = () =>
        qaHistory
            // drop unanswered entries (in-flight question, or a stream that
            // failed before any token arrived) — they carry no context
            .filter((item) => item.answer)
            .slice(-5)
            .map((item) => ({
                question: item.question,
                answer: item.answer,
            }));

    const startWebSocketStream = async (question, promptId = null, scope = null) => {
        stopStream();
        const replay = createChunkReplay((chunk) => appendAssistantChunk(reportId, channel, chunk));
        replayRef.current = replay;

        const accessToken = localStorage.getItem(token_keys.access_token);
        const historyForRAG = buildHistoryForRAG();

        const wsBaseUrl = getWebSocketUrl(BACKEND_BASE_URL);

        let documentIds = [];
        try {
            const data = await insightsApi.getDocuments(reportId);
            const docs = data.results || [];
            documentIds = docs.map(doc => doc.id);
        } catch (err) {
            console.error("Failed to fetch linked documents for WebSocket:", err);
        }

        // stopped (Close, history, unmount) or superseded while fetching the
        // documents — don't open a socket nobody will close
        if (replayRef.current !== replay) return;

        const ws = new WebSocket(
            `${wsBaseUrl}/ws/rag/`,
            ["Bearer", accessToken]
        );

        websocketRef.current = ws;
        setIsLoading(true);
        setAgentStatus(null);

        ws.onopen = () => {
            ws.send(JSON.stringify({
                report_ids: [reportId],
                user_input: question,
                history: historyForRAG,
                channel: 'insights_generation',
                session_id: sessionId,
                document_ids: documentIds,
                llm_provider: llmProvider,
                ...(promptId && { promptId }),
                ...(scope && { scope }),
            }));
        };

        // A stopped stream's frames must never reach the (new) chat
        const isCurrent = () => websocketRef.current === ws;

        ws.onmessage = (event) => {
            if (!isCurrent()) return;
            const data = JSON.parse(event.data);

            // Capture RAG citation documents whenever a frame carries them
            const retrievalDocs = data.retrieval?.documents;
            if (Array.isArray(retrievalDocs) && retrievalDocs.length > 0) {
                attachAssistantDocuments(reportId, channel, retrievalDocs);
            }

            // Risk summary: the BE sends risk_meta (scores/drivers/slice/n_quotes)
            // before the answer tokens. Store the scores on the assistant message
            // to render the score cards above the insight — never into the text.
            if (data.risk_meta) {
                attachAssistantRiskMeta(reportId, channel, data.risk_meta);
            }

            // The agent's current lookup: shown in the loading state, never in the text
            if ('status' in data) {
                setAgentStatus(data.status);
                return;
            }

            // A quick-prompt cache hit: the answer that follows is typed out
            // by the replay rather than shown at once (see chunkReplay)
            if (data.cached) {
                replay.markCached();
                return;
            }

            if (data.done) {
                setAgentStatus(null);
                ws.close();
                // a cached answer is still being revealed — finish it first
                replay.end(() => {
                    finalizeAssistantMessage(reportId, channel);
                    setIsLoading(false);
                });

                return;
            }

            if (!data.message) return;

            const chunk = data.message
                .split('\n\n')
                .map(p => p.replace('data: ', ''))
                .join('')
                .replaceAll('$n$l$', '\n');

            setAgentStatus(null);
            replay.push(chunk);
        };

        ws.onerror = () => {
            if (!isCurrent()) return;
            setError(intl.formatMessage({ id: 'askPrompta.connection_error', defaultMessage: 'Connection error.' }));
            setIsLoading(false);
        };

        ws.onclose = (event) => {
            if (!isCurrent()) return;
            // Risk answers emit no end-of-stream result frame, and the BE may
            // close the socket without a `done`. Finalize gracefully on close
            // so the message closes and the spinner clears — no stuck state,
            // no error on the missing references/citations frame. Scoped to
            // risk only; normal answers keep the unclean-only error path.
            if (scope === 'risk') {
                replay.end(() => {
                    finalizeAssistantMessage(reportId, channel);
                    setIsLoading(false);
                });
                return;
            }
            if (!event.wasClean && isLoading) {
                setError(intl.formatMessage({ id: 'askPrompta.connection_lost', defaultMessage: 'Connection lost.' }));
                setIsLoading(false);
            }
        }
    };

    const handleSend = () => {
        if (!userInput.trim() || isLoading) return;
        const question = userInput.trim();
        appendUserMessage(reportId, channel, question);
        setUserInput('');
        setIsLoading(true);
        setAgentStatus(null);
        startWebSocketStream(question, null, riskMode ? 'risk' : null);
    };

    const handleQuickPrompt = (qp) => {
        if (isLoading) return;
        // Quick prompts render no user bubble, so nothing seeds a qaHistory
        // slot for the streamed answer to accumulate into — the quick-prompt
        // answer was then absent from the history sent with a follow-up
        // question. Seed it here, keyed by the prompt id as question_text.
        appendQaHistoryEntry(reportId, channel, qp.id);
        if (qp.isRisk) {
            setRiskMode(true);
            startWebSocketStream('', qp.id, 'risk');
        } else {
            setRiskMode(false);
            startWebSocketStream('', qp.id);
        }
    };

    // Re-send the closest user question preceding the given message index
    const handleRegenerate = (index) => {
        if (isLoading) return;
        for (let i = index - 1; i >= 0; i--) {
            if (messages[i].role === 'user' && messages[i].content) {
                const question = messages[i].content;
                appendUserMessage(reportId, channel, question);
                setIsLoading(true);
                setAgentStatus(null);
                startWebSocketStream(question, null, riskMode ? 'risk' : null);
                return;
            }
        }
    };

    const handleFeedback = (index, message, vote) => {
        // re-voting the same way is a no-op — clearing a vote is not
        // supported by the feedback endpoint yet
        if (message.feedback === vote) return;
        const messageId = message.messageId || ensureMessageId(reportId, channel, index);
        setMessageFeedback(reportId, channel, messageId, vote);
    };

    const loadChatHistory = async () => {
        try {
            setLoadingHistory(true);
            setChatSessions(await insightsApi.getChatHistorySessions(reportId, channel));
        } catch {
            setError(intl.formatMessage({ id: 'askPrompta.load_history_failed', defaultMessage: 'Failed to load chat history' }));
        } finally {
            setLoadingHistory(false);
        }
    };

    const loadSessionMessages = async (sessionId) => {
        try {
            stopStream();
            setIsLoading(true);
            setAgentStatus(null);
            const rows = await insightsApi.getChatSessionMessages(sessionId, channel);
            const msgs = [];
            const history = [];

            rows.forEach(r => {
                msgs.push({ role: 'user', content: r.question });
                msgs.push({ role: 'assistant', content: r.answer });
                history.push({ question: r.question, answer: r.answer });
            });

            loadHistorySession(reportId, channel, sessionId, msgs, history);
            setSelectedSessionId(sessionId);
            setHistoryDrawerOpen(false);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        if (reportId) loadChatHistory();
        return () => stopStream();
    }, [reportId]);

    useEffect(() => {
        if (historyDrawerOpen && reportId) loadChatHistory();
    }, [historyDrawerOpen, reportId]);

    const handleKeyPress = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSend();
        }
    };

    const formatDate = (dateString) => {
        const date = new Date(dateString);
        const now = new Date();
        const diffTime = Math.abs(now - date);
        const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));

        if (diffDays === 0) return intl.formatMessage({ id: 'askPrompta.today', defaultMessage: 'Today' });
        if (diffDays === 1) return intl.formatMessage({ id: 'askPrompta.yesterday', defaultMessage: 'Yesterday' });
        if (diffDays < 7) return intl.formatMessage({ id: 'askPrompta.days_ago', defaultMessage: '{count} days ago' }, { count: diffDays });
        return date.toLocaleDateString();
    };

    const renderText = (text) => {
        if (!text) return null;
        return <ReactMarkdown remarkPlugins={[remarkGfm]}>{text}</ReactMarkdown>;
    };

    const handleDownloadClick = (event, message, index) => {
        setDownloadAnchor(event.currentTarget);
        setDownloadTarget({ content: message.content, index, riskMeta: message.riskMeta });
    };

    const handleDownloadClose = () => {
        setDownloadAnchor(null);
        setDownloadTarget(null);
    };

    const downloadAsPdf = async () => {
        if (!downloadTarget) return;
        const { content, index, riskMeta } = downloadTarget;
        handleDownloadClose();
        await exportAnswerAsPdf(content, `insight-answer-${index + 1}-${exportTimestamp()}.pdf`, { riskMeta });
    };

    const downloadAsWord = async () => {
        if (!downloadTarget) return;
        const { content, index, riskMeta } = downloadTarget;
        handleDownloadClose();
        await exportAnswerAsWord(content, `insight-answer-${index + 1}-${exportTimestamp()}.docx`, { riskMeta });
    };

    return (
        <>
            <Card>
                <CardContent>
                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            mb: 2,
                        }}
                    >
                        <Typography
                            variant="h6"
                            sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                        >
                            <FormattedMessage id="askPrompta.insight_generation" defaultMessage="Insight Generation" />
                            {!isConnected && (
                                <Typography variant="caption" color="error">
                                    <FormattedMessage id="askPrompta.disconnected" defaultMessage="(Disconnected)" />
                                </Typography>
                            )}
                            {(isLoading || messages.length !== 0 || isViewingHistory) && (
                                <Chip
                                    label={intl.formatMessage({ id: 'askPrompta.viewing_history', defaultMessage: 'Close' })}
                                    size="small"
                                    color="primary"
                                    variant="outlined"
                                    onDelete={() => {
                                        // abort an answer still streaming or typing out,
                                        // then start clean
                                        stopStream();
                                        setIsLoading(false);
                                        setSelectedSessionId(null);
                                        setRiskMode(false);
                                        initSession(reportId, channel);
                                    }}
                                />
                            )}
                        </Typography>
                        <Button
                            startIcon={<HistoryIcon />}
                            onClick={() => setHistoryDrawerOpen(true)}
                            variant="outlined"
                            size="small"
                        >
                            <FormattedMessage id="askPrompta.history" defaultMessage="History" />
                        </Button>
                    </Box>

                    {error && (
                        <Alert
                            severity="error"
                            sx={{ mb: 2 }}
                            onClose={() => setError(null)}
                        >
                            {error}
                        </Alert>
                    )}

                    {/* Messages Container */}
                    <Paper
                        ref={scrollContainerRef}
                        onScroll={handleScroll}
                        elevation={0}
                        sx={{
                            maxHeight: '400px',
                            minHeight: '200px',
                            overflowY: 'auto',
                            mb: 2,
                            p: 2,
                            bgcolor: 'background.default',
                            border: '1px solid',
                            borderColor: 'divider',
                        }}
                    >
                        {!isLoading && messages.length === 0 && !isViewingHistory && (
                            <Box sx={{ py: 2 }}>
                                {/* View toggle */}
                                <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
                                    <ModernToggle
                                        leftLabel={intl.formatMessage({ id: 'askPrompta.quick_prompts', defaultMessage: 'Actions' })}
                                        rightLabel={intl.formatMessage({ id: 'askPrompta.topics', defaultMessage: 'Topics' })}
                                        checked={showTopics}
                                        onChange={setShowTopics}
                                        size="small"
                                    />
                                </Box>

                                {!showTopics ? (
                                    /* ── Quick Prompts view ── */
                                    <Box>
                                        <Box
                                            sx={{
                                                display: 'grid',
                                                gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                                                gap: 1.5,
                                            }}
                                        >
                                            {QUICK_PROMPTS
                                                .filter((qp) => !qp.isRisk || hasRiskData)
                                                .map((qp) => {
                                                const Icon = qp.icon;
                                                return (
                                                    <Paper
                                                        key={qp.id}
                                                        elevation={0}
                                                        onClick={() => handleQuickPrompt(qp)}
                                                        sx={{
                                                            p: 2,
                                                            cursor: 'pointer',
                                                            border: `1px solid ${qp.border}`,
                                                            background: qp.bg,
                                                            borderRadius: 2,
                                                            display: 'flex',
                                                            alignItems: 'flex-start',
                                                            gap: 1.5,
                                                            transition: 'all 0.15s ease',
                                                            '&:hover': {
                                                                borderColor: qp.color,
                                                                background: qp.bg,
                                                                boxShadow: `0 2px 8px ${qp.border}`,
                                                                transform: 'translateY(-1px)',
                                                            },
                                                        }}
                                                    >
                                                        <Box
                                                            sx={{
                                                                width: 36,
                                                                height: 36,
                                                                borderRadius: 1.5,
                                                                bgcolor: qp.bg,
                                                                border: `1px solid ${qp.border}`,
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                justifyContent: 'center',
                                                                flexShrink: 0,
                                                            }}
                                                        >
                                                            <Icon sx={{ fontSize: 18, color: qp.color }} />
                                                        </Box>
                                                        <Box>
                                                            <Typography variant="body2" sx={{ fontWeight: 600, color: qp.color, lineHeight: 1.3 }}>
                                                                <FormattedMessage id={qp.labelId} defaultMessage={qp.labelDefault} />
                                                            </Typography>
                                                            <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.4, display: 'block', mt: 0.25 }}>
                                                                <FormattedMessage id={qp.descriptionId} defaultMessage={qp.descriptionDefault} />
                                                            </Typography>
                                                        </Box>
                                                    </Paper>
                                                );
                                            })}
                                        </Box>
                                    </Box>
                                ) : (
                                    /* ── Topics view ── */
                                    themes.length > 0 ? (
                                        <Box>

                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <IconButton
                                                    size="small"
                                                    onClick={handlePrevThemes}
                                                    disabled={currentThemePage === 0}
                                                    sx={{
                                                        visibility: totalThemePages > 1 ? 'visible' : 'hidden',
                                                        bgcolor: 'background.paper',
                                                        border: '1px solid',
                                                        borderColor: 'divider',
                                                        '&:hover': { bgcolor: 'action.hover' },
                                                        '&:disabled': { opacity: 0.3 }
                                                    }}
                                                >
                                                    <ChevronLeftIcon />
                                                </IconButton>

                                                <Box sx={{ flex: 1, display: 'flex', gap: 2, justifyContent: 'center' }}>
                                                    {currentThemes.map((theme, index) => (
                                                        <Paper
                                                            key={index}
                                                            elevation={2}
                                                            sx={{
                                                                flex: 1,
                                                                maxWidth: 300,
                                                                p: 2,
                                                                borderRadius: 2,
                                                                border: '1px solid',
                                                                borderColor: 'primary.light',
                                                                bgcolor: 'background.paper',
                                                                transition: 'all 0.2s ease',
                                                                '&:hover': {
                                                                    boxShadow: 4,
                                                                    borderColor: 'primary.main',
                                                                }
                                                            }}
                                                        >
                                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
                                                                <Chip
                                                                    label={theme.pillar}
                                                                    size="small"
                                                                    color="primary"
                                                                    variant="outlined"
                                                                    sx={{ fontSize: '0.65rem', height: 20 }}
                                                                />
                                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                                    <PeopleIcon sx={{ fontSize: 12, color: 'text.disabled' }} />
                                                                    <Typography variant="caption" sx={{ color: 'text.disabled', fontSize: '0.65rem' }}>
                                                                        {theme.num_of_responses}
                                                                    </Typography>
                                                                </Box>
                                                            </Box>

                                                            <Typography
                                                                variant="subtitle2"
                                                                sx={{
                                                                    fontWeight: 600,
                                                                    mb: 1,
                                                                    color: 'text.primary',
                                                                    display: 'flex',
                                                                    alignItems: 'center',
                                                                    gap: 0.5,
                                                                    fontSize: '0.85rem'
                                                                }}
                                                            >
                                                                {theme.topic_text}
                                                            </Typography>

                                                            <Typography
                                                                variant="body2"
                                                                sx={{
                                                                    mb: 1.5,
                                                                    lineHeight: 1.5,
                                                                    color: 'text.primary',
                                                                    fontWeight: 250,
                                                                    fontSize: '0.8rem',
                                                                    fontStyle: 'italic',
                                                                    overflow: 'hidden',
                                                                    textOverflow: 'ellipsis',
                                                                    display: '-webkit-box',
                                                                    WebkitLineClamp: 2,
                                                                    WebkitBoxOrient: 'vertical'
                                                                }}
                                                            >
                                                                <FormattedMessage id="askPrompta.question_prefix" defaultMessage="Q:" /> {theme.question_text}
                                                            </Typography>

                                                            {theme.keywords.length > 0 && (
                                                                <Typography
                                                                    variant="caption"
                                                                    sx={{
                                                                        display: 'block',
                                                                        mb: 1.5,
                                                                        color: 'text.disabled',
                                                                        fontSize: '0.6rem',
                                                                        fontStyle: 'italic'
                                                                    }}
                                                                >
                                                                    <FormattedMessage id="askPrompta.keywords" defaultMessage="Keywords" />: {theme.keywords.slice(0, 5).join(' • ')}
                                                                </Typography>
                                                            )}

                                                            <Divider sx={{ mb: 1 }} />
                                                            <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary', display: 'block', mb: 0.5, fontSize: '0.65rem' }}>
                                                                <FormattedMessage id="askPrompta.ask_about_this" defaultMessage="Ask about this:" />
                                                            </Typography>
                                                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                                                {theme.suggested_questions.map((question, idx) => (
                                                                    <Button
                                                                        key={idx}
                                                                        size="small"
                                                                        variant="text"
                                                                        onClick={() => handleSuggestedQuestion(question)}
                                                                        sx={{
                                                                            textTransform: 'none',
                                                                            justifyContent: 'flex-start',
                                                                            fontSize: '0.7rem',
                                                                            p: 0.5,
                                                                            minHeight: 'auto',
                                                                            color: 'primary.main',
                                                                            textAlign: 'left',
                                                                            lineHeight: 1.3,
                                                                            '&:hover': {
                                                                                bgcolor: 'primary.50',
                                                                            }
                                                                        }}
                                                                    >
                                                                        {question}
                                                                    </Button>
                                                                ))}
                                                            </Box>
                                                        </Paper>
                                                    ))}
                                                </Box>

                                                <IconButton
                                                    size="small"
                                                    onClick={handleNextThemes}
                                                    disabled={currentThemePage >= totalThemePages - 1}
                                                    sx={{
                                                        visibility: totalThemePages > 1 ? 'visible' : 'hidden',
                                                        bgcolor: 'background.paper',
                                                        border: '1px solid',
                                                        borderColor: 'divider',
                                                        '&:hover': { bgcolor: 'action.hover' },
                                                        '&:disabled': { opacity: 0.3 }
                                                    }}
                                                >
                                                    <ChevronRightIcon />
                                                </IconButton>
                                            </Box>

                                            {totalThemePages > 1 && (
                                                <Box sx={{ display: 'flex', justifyContent: 'center', gap: 0.5, mt: 2 }}>
                                                    {Array.from({ length: totalThemePages }).map((_, idx) => (
                                                        <Box
                                                            key={idx}
                                                            sx={{
                                                                width: 8,
                                                                height: 8,
                                                                borderRadius: '50%',
                                                                bgcolor: idx === currentThemePage ? 'primary.main' : 'grey.300',
                                                                transition: 'bgcolor 0.2s ease',
                                                                cursor: 'pointer',
                                                                '&:hover': { bgcolor: idx === currentThemePage ? 'primary.main' : 'grey.400' }
                                                            }}
                                                            onClick={() => setCurrentThemePage(idx)}
                                                        />
                                                    ))}
                                                </Box>
                                            )}
                                        </Box>
                                    ) : (
                                        <Typography
                                            variant="body2"
                                            color="text.secondary"
                                            sx={{ textAlign: 'center', mt: 4 }}
                                        >
                                            <FormattedMessage id="askPrompta.ask_placeholder" defaultMessage="Ask me anything about this report..." />
                                        </Typography>
                                    )
                                )}
                            </Box>
                        )}

                        {messages.map((message, index) => (
                            // Render guard: an assistant message with no
                            // content and no active stream would paint an
                            // empty white bubble — skip it entirely.
                            message.role === 'assistant' && !message.content && !message.isStreaming ? null : (
                            <Box key={index} sx={{ mb: 2 }}>
                                <ChatMessageMeta message={message} />
                                <Box
                                    sx={{
                                        display: 'flex',
                                        justifyContent:
                                            message.role === 'user'
                                                ? 'flex-end'
                                                : 'flex-start',
                                    }}
                                >
                                    <Paper
                                    elevation={1}
                                    sx={{
                                        p: 1.5,
                                        maxWidth: '80%',
                                        bgcolor:
                                            message.role === 'user'
                                                ? 'primary.main'
                                                : 'background.paper',
                                        color:
                                            message.role === 'user'
                                                ? 'primary.contrastText'
                                                : 'text.primary',
                                        position: 'relative',
                                        // GFM tables (e.g. the risk Summary table) render
                                        // borderless by default — give assistant answers the
                                        // same bordered table styling as RisksSection.
                                        ...(message.role === 'assistant' && {
                                            overflowX: 'auto',
                                            '& table': { width: '100%', borderCollapse: 'collapse', fontSize: 13, my: 1 },
                                            '& th, & td': { border: '0.5px solid rgba(21,101,192,0.15)', padding: '6px 10px', textAlign: 'left' },
                                            '& th': { background: 'rgba(21,101,192,0.06)', color: '#0d47a1', fontWeight: 500 },
                                        }),
                                    }}
                                >
                                    {/* Risk-summary score cards, above the streamed insight */}
                                    {message.role === 'assistant' &&
                                        message.riskMeta?.scores &&
                                        Object.keys(message.riskMeta.scores).length > 0 && (
                                            <RiskScoreCards scores={message.riskMeta.scores} />
                                        )}
                                    {message.role === 'assistant' && message.isStreaming && !message.content ? (
                                        <TypingIndicator showStatus status={agentStatus} />
                                    ) : (
                                        <>
                                            {renderText(message.content)}
                                            {message.role === 'assistant' && message.isStreaming && (
                                                <span style={{ marginLeft: 4, opacity: 0.5 }}>▊</span>
                                            )}
                                        </>
                                    )}
                                    </Paper>
                                </Box>
                                {message.role === 'assistant' && !message.isStreaming && (
                                    <MessageActionsRow
                                        message={message}
                                        references={<Citations documents={message.documents} />}
                                        onRegenerate={() => handleRegenerate(index)}
                                        onFeedback={(vote) => handleFeedback(index, message, vote)}
                                        disabled={isLoading}
                                        extras={
                                            message.content ? (
                                                <Tooltip title={intl.formatMessage({ id: 'askPrompta.download', defaultMessage: 'Export' })}>
                                                    <IconButton
                                                        size="small"
                                                        onClick={(e) => handleDownloadClick(e, message, index)}
                                                        aria-label={intl.formatMessage({ id: 'askPrompta.download', defaultMessage: 'Export' })}
                                                        sx={{
                                                            width: 28,
                                                            height: 28,
                                                            color: 'text.disabled',
                                                            '&:hover': { bgcolor: 'action.hover', color: 'text.secondary' },
                                                        }}
                                                    >
                                                        <DownloadIcon sx={{ fontSize: 15 }} />
                                                    </IconButton>
                                                </Tooltip>
                                            ) : null
                                        }
                                    />
                                )}
                                {message.role === 'assistant' && !message.isStreaming && index < messages.length - 1 && (
                                    <Divider sx={{ mt: 1.5, opacity: 0.5 }} />
                                )}
                            </Box>
                            )
                        ))}

                        {isLoading && !messages.some(m => m.isStreaming) && (
                            <Box
                                sx={{
                                    display: 'flex',
                                    justifyContent: 'flex-start',
                                    mb: 2,
                                }}
                            >
                                <Paper elevation={1} sx={{ p: 1.5 }}>
                                    <TypingIndicator showStatus status={agentStatus} />
                                </Paper>
                            </Box>
                        )}

                        <div ref={messagesEndRef} />
                    </Paper>

                    {/* Input Field */}
                    <Box>
                        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                            <ChatSettingsButton
                                provider={llmProvider}
                                onChange={setLlmProvider}
                                disabled={isLoading}
                            />
                            <TextField
                                fullWidth
                                multiline
                                maxRows={4}
                                placeholder={intl.formatMessage({ id: 'askPrompta.type_placeholder', defaultMessage: 'Type your question here...' })}
                                value={userInput}
                                onChange={(e) => setUserInput(e.target.value)}
                                onKeyPress={handleKeyPress}
                                disabled={!isConnected || isLoading}
                                variant="outlined"
                                size="small"
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        borderRadius: 2,
                                        minHeight: 48,
                                    },
                                }}
                            />
                            <IconButton
                                color="primary"
                                onClick={handleSend}
                                disabled={!userInput.trim() || isLoading}
                                sx={{
                                    width: 32,
                                    height: 32,
                                    bgcolor: 'primary.main',
                                    color: 'white',
                                    '&:hover': {
                                        bgcolor: 'primary.dark',
                                    },
                                    '&:disabled': {
                                        bgcolor: 'action.disabledBackground',
                                    },
                                }}
                            >
                                <SendIcon sx={{ fontSize: 16 }} />
                            </IconButton>
                        </Box>
                        <Typography sx={{ fontSize: 10.5, color: 'text.disabled', mt: 0.5, ml: 0.5 }}>
                            <FormattedMessage id="chatbot.input_hint" defaultMessage="Press ↵ to send · Shift+↵ for newline" />
                        </Typography>
                    </Box>
                </CardContent>
            </Card>

            {/* Chat History Drawer */}
            <Drawer
                anchor="right"
                open={historyDrawerOpen}
                onClose={() => setHistoryDrawerOpen(false)}
                sx={{
                    '& .MuiDrawer-paper': {
                        width: { xs: '100%', sm: 400 },
                        maxWidth: '100%',
                    },
                }}
            >
                <Box sx={{ p: 2 }}>
                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            mb: 2,
                        }}
                    >
                        <Typography
                            variant="h6"
                            sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                        >
                            <HistoryIcon />
                            <FormattedMessage id="askPrompta.chat_history" defaultMessage="Chat History" />
                        </Typography>
                        <IconButton
                            onClick={() => setHistoryDrawerOpen(false)}
                            size="small"
                        >
                            <CloseIcon />
                        </IconButton>
                    </Box>

                    {loadingHistory ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                            <CircularProgress />
                        </Box>
                    ) : chatSessions.length === 0 ? (
                        <Box sx={{ textAlign: 'center', py: 4 }}>
                            <Typography variant="body2" color="text.secondary">
                                <FormattedMessage id="askPrompta.no_chat_history" defaultMessage="No chat history available" />
                            </Typography>
                        </Box>
                    ) : (
                        <List sx={{ px: 0 }}>
                            {chatSessions.map((session, index) => (
                                <React.Fragment key={session.session_id}>
                                    <ListItem disablePadding>
                                        <ListItemButton
                                            onClick={() =>
                                                loadSessionMessages(session.session_id)
                                            }
                                            sx={{
                                                borderRadius: 1,
                                                mb: 1,
                                                border: '1px solid',
                                                borderColor:
                                                    selectedSessionId ===
                                                        session.session_id
                                                        ? 'primary.main'
                                                        : 'divider',
                                                bgcolor:
                                                    selectedSessionId ===
                                                        session.session_id
                                                        ? 'primary.50'
                                                        : 'transparent',
                                                '&:hover': {
                                                    bgcolor: 'action.hover',
                                                },
                                            }}
                                        >
                                            <Box sx={{ width: '100%' }}>
                                                <Box
                                                    sx={{
                                                        display: 'flex',
                                                        alignItems: 'flex-start',
                                                        gap: 1,
                                                        mb: 0.5,
                                                    }}
                                                >
                                                    <ChatIcon
                                                        sx={{
                                                            fontSize: 18,
                                                            mt: 0.5,
                                                            color: 'primary.main',
                                                        }}
                                                    />
                                                    <Box
                                                        sx={{ flex: 1, minWidth: 0 }}
                                                    >
                                                        <Typography
                                                            variant="body2"
                                                            sx={{
                                                                fontWeight: 'medium',
                                                                overflow: 'hidden',
                                                                textOverflow:
                                                                    'ellipsis',
                                                                display:
                                                                    '-webkit-box',
                                                                WebkitLineClamp: 2,
                                                                WebkitBoxOrient:
                                                                    'vertical',
                                                                lineHeight: 1.4,
                                                            }}
                                                        >
                                                            {session.first_question}
                                                        </Typography>
                                                        <Typography
                                                            variant="caption"
                                                            color="text.secondary"
                                                            sx={{
                                                                display: 'block',
                                                                mt: 0.5,
                                                            }}
                                                        >
                                                            {formatDate(session.date)}
                                                        </Typography>
                                                    </Box>
                                                </Box>
                                            </Box>
                                        </ListItemButton>
                                    </ListItem>
                                    {index < chatSessions.length - 1 && (
                                        <Divider sx={{ my: 1 }} />
                                    )}
                                </React.Fragment>
                            ))}
                        </List>
                    )}
                </Box>
            </Drawer>

            <AnswerExportMenu
                anchorEl={downloadAnchor}
                onClose={handleDownloadClose}
                onPdf={downloadAsPdf}
                onWord={downloadAsWord}
            />
        </>
    );
};

export default AskPrompta;







